import { isAbsoluteHttpUrl } from '@/lib/utils/link-utils'
import type { DOAErrorStatus } from 'app/(web)/(account)/(authentication)/account/doa/page'

/**
 * URL to go to the user account information.
 */
export const toUserInformation = '/customer/user-information'

/**
 * URL to go to the login page.
 * if email is provided, a warning message will appear on the login form
 */
export function toLoginPage(withEmail?: boolean) {
  const withEmailParam = withEmail ? `?email=true` : ''

  return `/customer/account/login${withEmailParam}`
}

/**
 * URL to go to the account selection page.
 */
export const toAccountSelectionPage = '/account/selection'

/**
 * URL to go to the favorite point of sale selection page.
 */
export const toPointOfSaleSelectionPage = '/account/point-of-sale-selection'

/**
 * URL to go to the home page.
 */
export const toHomePage = '/'

/**
 * URL to go to the SIRET page.
 * if no email is provided, the user will be redirected to the 404 page
 */
export const toCompanyNationalIdPage = '/account/create'

export const loginGenericErrorReason = ['subscription', 'signin'] as const

type LoginGenericErrorReason = (typeof loginGenericErrorReason)[number]

export function isLoginGenericErrorReason(reason: string): reason is LoginGenericErrorReason {
  return loginGenericErrorReason.includes(reason as LoginGenericErrorReason)
}

export function toLoginGenericErrorPage(reasonForTracking: LoginGenericErrorReason) {
  const url = '/login/error'
  const params = new URLSearchParams()

  params.set('reason', reasonForTracking)

  return url
}

export function toDOAErrorPage(
  status?: DOAErrorStatus,
  company?: { id: string; companyNationalId: string },
) {
  let url = '/account/doa'

  const params = new URLSearchParams()

  if (status) params.set('status', status)

  if (status === 'active') {
    if (company?.companyNationalId) params.set('companyNationalId', company?.companyNationalId)
    if (company?.id) params.set('id', company?.id)
  }

  if (params.toString()) {
    url += `?${params.toString()}`
  }

  return url
}

export const toRegistrationInProgressPage = (process: 'doa' | 'doc') =>
  `/account/create/in-progress?process=${process}`

/**
 * Check if the given path is related to account management (dashboard, favorites, etc.)
 * These paths should typically be avoided when redirecting after a login.
 */
export function isAccountRelatedPath(path: string): boolean {
  let pathname = path

  if (isAbsoluteHttpUrl(path)) {
    try {
      pathname = new URL(path).pathname
    } catch (error) {
      console.error('Error during login redirect:', error)
    }
  }

  return (
    pathname.startsWith('/customer') ||
    pathname.startsWith('/account') ||
    pathname.startsWith('/login')
  )
}

/**
 * Check if the given path requires the user to have selected a favorite point of sale.
 * Returns true for catalog and checkout paths, false for auth/account/technical pages.
 */
export function needsPickupPointOfSale(pathname: string): boolean {
  return !(
    pathname.startsWith('/account/') ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/customer/account/login') ||
    pathname.startsWith('/aop/')
  )
}
